/**
 * TypeScript types for EdgeDocs Typesetter
 * Matches Rust types from typesetter_core
 */

import { fontResolver } from '../../services/FontResolver';

// Direction for text layout
export type Direction = 'LTR' | 'RTL' | 'TTB' | 'BTT';

// Font data (actual font file bytes)
export interface FontData {
  font_uid: string;       // Unique font identifier (matches FontFingerprint.font_uid)
  bytes: Uint8Array;      // Font file bytes (TTF/OTF) - gets serialized to number[] by postMessage
}

// Font fingerprint for deterministic font resolution
export interface FontFingerprint {
  font_uid: string;           // "Inter-Regular-v3.0"
  postscript_name: string;    // PostScript name
  family: string;             // Font family
  weight: number;             // CSS weight: 100-900 (400 = Regular, 700 = Bold)
  style: string;              // "normal" | "italic"
}

// Variable-font axis
export interface FontAxis {
  tag: string;    // "wght", "wdth", "slnt"
  value: number;  // Axis value
}

// Font specification
export interface FontSpec {
  fingerprint: FontFingerprint;
  size_fp: number;                // Font size in 26.6 fixed-point
  axes?: FontAxis[] | null;       // Variable-font axes
  features?: string[] | null;     // OpenType features
}

// Text run (before shaping)
export interface TextRun {
  text: string;
  font: FontSpec;
  script: string;     // ISO 15924: "Latn", "Arab", "Deva"
  lang: string;       // BCP 47: "en", "ar", "hi"
  dir: Direction;
  features?: string[] | null;
}

// Glyph (after shaping)
export interface Glyph {
  id: number;          // Glyph ID from font
  x_fp: number;        // X position (26.6)
  y_fp: number;        // Y position (26.6)
  advance_fp: number;  // Horizontal advance width (26.6) - for line breaking
  cluster: number;     // Grapheme cluster index (NOT UTF-16!)
}

// Shaped run (after HarfBuzz)
export interface ShapedRun {
  glyphs: Glyph[];
  bidi_level: number;
  script: string;
  lang: string;
  dir: Direction;
  font_fingerprint: FontFingerprint;
  font_size_fp: number;
  grapheme_count: number;
  
  // ✅ NEW: Self-contained segment fields
  text: string;                       // Text for THIS segment (sliced, not full original)
  source_byte_start: number;          // Byte offset in original document (for back-mapping)
  source_byte_end: number;            // Byte offset end in original document
  grapheme_x_positions: number[];     // X position before each grapheme + after last (length = grapheme_count + 1)
  
  // ⚠️ DEPRECATED: Kept for backward compatibility during migration
  original_text: string;              // Use `text` instead
  cluster_advances_fp: number[];      // Use `grapheme_x_positions` instead
  grapheme_to_cluster: number[];      // Use `grapheme_x_positions` instead
}

// Caret position (grapheme-indexed)
export interface CaretPosition {
  grapheme_index: number;
  x_fp: number;
  y_fp: number;        // ✅ NEW: Y position for this caret (26.6 fixed-point)
  is_rtl: boolean;
}

// Line of text (after line-breaking)
export interface Line {
  runs: ShapedRun[];
  baseline_y_fp: number;
  width_fp: number;
  height_fp: number;
  ascent_fp: number;
  descent_fp: number;
  line_id: string;                // Unique ID for this line (for debugging)
  x_offset_fp: number;            // Horizontal position (left margin offset)
  start_grapheme_index: number;   // ✅ NEW: First grapheme index in this line (document-global)
  end_grapheme_index: number;     // ✅ NEW: Last grapheme index in this line (document-global, inclusive)
  ends_with_newline: boolean;     // ✅ NEW: True if line ends with \n (for caret_map)
  ends_with_page_break?: boolean; // ✅ NEW: True if line ends with \f (Ctrl+Enter page break)
}

// Engine versions for reproducibility
export interface EngineVersions {
  core_version: string;       // "1.0.0"
  hb_version: string;          // "12.0.0"
  rules_version: string;       // "Unicode-15.0"
  font_set_version: string;    // User's font set version
}

// Paragraph layout input
export interface ParagraphLayoutInput {
  text_runs: TextRun[];
  line_width_fp: number;       // 26.6 fixed-point (content width)
  origin_x_fp: number;         // 26.6 fixed-point (left margin offset)
  origin_y_fp?: number;        // 26.6 fixed-point (top margin offset) - optional for pagination
  alignment: string;           // "left" | "right" | "center" | "justify"
  engine_versions: EngineVersions;
  fonts: FontData[];           // Font file bytes for HarfBuzz (Phase 3)
}

// Paragraph layout snapshot
export interface ParagraphSnapshot {
  lines: Line[];
  caret_map: CaretPosition[];
  total_width_fp: number;
  total_height_fp: number;
  plan_hash: string;           // SHA256 of fixed-point geometry
  source: 'authoritative' | 'provisional';
  snapshot_id: string;         // UUID - changes on every re-layout (versioning)
  paragraph_id: string;        // Stable ID - persists across re-layouts (for merge matching)
  engine_versions: EngineVersions;
}

/**
 * Fixed-point conversion utilities (26.6 format)
 */
export const FixedPoint = {
  /** Convert pixels to 26.6 fixed-point */
  fromPx: (px: number): number => Math.round(px * 64),
  
  /** Convert 26.6 fixed-point to pixels */
  toPx: (fp: number): number => fp / 64,
  
  /** Multiply two fixed-point numbers */
  multiply: (a_fp: number, b_fp: number): number => {
    return Math.round((a_fp * b_fp) / 64);
  },
  
  /** Divide two fixed-point numbers */
  divide: (a_fp: number, b_fp: number): number => {
    return Math.round((a_fp * 64) / b_fp);
  },
};

/**
 * Default engine versions
 */
export const DEFAULT_ENGINE_VERSIONS: EngineVersions = {
  core_version: '1.0.0',
  hb_version: '12.0.0',
  rules_version: 'Unicode-15.0',
  font_set_version: 'production-v1'
};

/**
 * Create a basic TextRun with Font Manager integration
 * 
 * CRITICAL: This function is synchronous to maintain pagination performance!
 * Font resolution uses pre-warmed cache from FontResolver (see FontWarmupService).
 * 
 * @param text - Text content for this run
 * @param fontFamily - Font family name (e.g., "Carlito", "Times New Roman")
 * @param fontSize - Font size in pixels
 * @param fontWeight - Font weight (100-900, default 400 for Regular)
 * @param fontStyle - Font style ("normal", "italic", "oblique")
 * @param options - Additional TextRun properties to override
 */
export function createTextRun(
  text: string,
  fontFamily: string = 'Roboto',  // ✅ System font that exists in database
  fontSize: number = 12,
  fontWeight: number = 400,
  fontStyle: 'normal' | 'italic' | 'oblique' = 'normal',
  options: Partial<TextRun> = {}
): TextRun {
  // ✅ Use FontResolver for synchronous font resolution (ESM import)
  // FontResolver.warmCache() must be called before any createTextRun() calls
  // (handled by FontWarmupService on app mount)
  
  // Resolve font_uid using Font Manager cache (synchronous!)
  const font_uid = fontResolver.resolveFontUidSync(fontFamily, fontWeight, fontStyle);
  
  if (process.env.NODE_ENV !== 'production') {
    console.log(`[createTextRun] ${fontFamily} ${fontWeight} ${fontStyle} → ${font_uid}`);
  }

  return {
    text,
    font: {
      fingerprint: {
        font_uid,                    // ✅ Dynamic from Font Manager
        postscript_name: font_uid,   // ✅ Same as font_uid
        family: fontFamily,          // ✅ User-selected family
        weight: fontWeight,          // ✅ Dynamic weight (not hardcoded 400!)
        style: fontStyle,            // ✅ Dynamic style (not hardcoded 'normal'!)
      },
      size_fp: FixedPoint.fromPx(fontSize),
      axes: null,
      features: null,
    },
    script: 'Latn',
    lang: 'en',
    dir: 'LTR',
    features: null,
    ...options
  };
}

/**
 * Create a basic ParagraphLayoutInput
 */
export function createParagraphLayoutInput(
  text: string,
  lineWidthPx: number = 400,
  options: Partial<ParagraphLayoutInput> = {}
): ParagraphLayoutInput {
  return {
    text_runs: [createTextRun(text)],
    line_width_fp: FixedPoint.fromPx(lineWidthPx),
    origin_x_fp: 0, // Default: no margin offset
    alignment: 'left',
    engine_versions: DEFAULT_ENGINE_VERSIONS,
    fonts: [], // Default: empty fonts array (will be populated by worker)
    ...options
  };
}

// ============================================================================
// PAGINATION TYPES (NEW for Phase 1)
// ============================================================================

/**
 * Cursor into document pagination state
 * Represents a position in the document for resumable pagination
 */
export interface PageCursor {
  /** Paragraph index (0-based) */
  para_ix: number;
  /** Line index within paragraph (0-based) */
  line_ix: number;
}

/**
 * Options for pagination behavior
 */
export interface PaginateOptions {
  /** Whether to render line numbers */
  line_numbers: boolean;
  /** Number of columns (always 1 for now) */
  columns: number;
}

/**
 * Slice of a paragraph that appears on a page
 */
export interface ParagraphSlice {
  /** ID of the source paragraph */
  paragraph_id: string;
  /** First line index (inclusive) */
  line_start: number;
  /** Last line index (exclusive) */
  line_end: number;
}

/**
 * Rectangle in fixed-point coordinates
 */
export interface Rect {
  x_fp: number;
  y_fp: number;
  w_fp: number;
  h_fp: number;
}

/**
 * Layout information for a single page
 */
export interface PageLayout {
  /** Page number (0-based) */
  page_index: number;
  /** Content area (inside margins) in fixed-point */
  content_rect_fp: Rect;
  /** Paragraph slices on this page */
  paragraphs: ParagraphSlice[];
  /** Hash of page content (for change detection) */
  page_hash: string;
  /** Hash algorithm used */
  page_hash_alg: string;
}

/**
 * Performance telemetry from pagination
 */
export interface PaginationTelemetry {
  /** Number of pages generated */
  pages_emitted: number;
  /** Number of lines scanned */
  lines_scanned: number;
  /** Number of paragraphs processed */
  paragraphs_processed: number;
  /** Number of times pagination resumed from spill frontier */
  spill_resumes: number;
  /** Peak memory usage in bytes */
  mem_bytes_peak: number;
}

/**
 * Paragraph height information from WASM (Phase 0 Height-Index)
 * Matches the ParagraphHeightInfo struct in typesetter/wasm/src/lib.rs
 */
export interface ParagraphHeightInfo {
  /** Stable paragraph ID (from AST or generated) */
  paragraph_id: string;
  /** Height in fixed-point units (1/64th pixel) */
  height_fp: number;
  /** Height in pixels (for convenience) */
  height_px: number;
  /** Number of lines in this paragraph */
  line_count: number;
  /** Paragraph index in document order */
  index: number;
}

/**
 * Result from windowed pagination
 */
export interface PageWindowResult {
  /** Array of page layouts */
  pages: PageLayout[];
  /** Cursor for next page (null if at end) */
  next_cursor: PageCursor | null;
  /** Total number of pages in the document (for UI to render correct number of containers) */
  total_pages: number;
  /** Performance metrics */
  telemetry: PaginationTelemetry;
  /** Full document paragraph snapshot (includes caret_map for interactions) */
  snapshot: ParagraphSnapshot;
  /** Page index where early termination occurred (null if no early termination) - NEW for Phase 1 */
  stopped_at_page?: number | null;
  /** Paragraph heights for streaming pagination (Phase 0 Height-Index) */
  paragraph_heights?: ParagraphHeightInfo[];
}

/**
 * Page size (named or custom)
 */
export type PageSize = 
  | string  // Named: "Letter", "A4", "Legal"
  | { w_px: number; h_px: number };  // Custom dimensions

/**
 * Margins in fixed-point
 */
export interface Margins {
  top: number;    // Fixed-point
  right: number;  // Fixed-point
  bottom: number; // Fixed-point
  left: number;   // Fixed-point
}

/**
 * Page orientation
 */
export type Orientation = 'portrait' | 'landscape';

/**
 * Page setup (dimensions and margins)
 * MUST MATCH Rust PageSetup struct in page_setup.rs
 */
export interface PageSetup {
  /** Page size (named or custom) */
  page_size: PageSize;
  /** Margins in fixed-point */
  margins_fp: Margins;
  /** Number of columns (1-4) */
  columns: number;
  /** Page orientation */
  orientation: Orientation;
  /** Content rectangle in fixed-point (calculated from margins) */
  content_rect_fp: Rect;
}

/**
 * Input for document-level pagination
 */
export interface DocumentLayoutInput {
  /** Array of paragraphs to layout */
  paragraphs: ParagraphLayoutInput[];
  /** Page dimensions and margins */
  page_setup: PageSetup;
  /** Pagination options */
  paginate_options?: PaginateOptions;
  /** Starting cursor (for windowed pagination) */
  start_cursor?: PageCursor;
  /** Maximum pages to return */
  max_pages?: number;
  /** Engine versions for caching */
  engine_versions: EngineVersions;
}

// ============================================================================
// STANDARD PAGE SIZES
// ============================================================================

export const PAGE_SIZES = {
  LETTER: {
    page_size: "Letter",
    margins_fp: {
      top: FixedPoint.fromPx(72),    // 0.75 inch at 96 DPI
      right: FixedPoint.fromPx(72),
      bottom: FixedPoint.fromPx(72),
      left: FixedPoint.fromPx(72),
    },
    columns: 1,
    orientation: 'portrait' as Orientation,
    content_rect_fp: {
      x_fp: FixedPoint.fromPx(72),
      y_fp: FixedPoint.fromPx(72),
      w_fp: FixedPoint.fromPx(672),  // 816 - 72*2 (Letter at 96 DPI)
      h_fp: FixedPoint.fromPx(912),  // 1056 - 72*2
    }
  },
  A4: {
    page_size: "A4",
    margins_fp: {
      top: FixedPoint.fromPx(72),
      right: FixedPoint.fromPx(72),
      bottom: FixedPoint.fromPx(72),
      left: FixedPoint.fromPx(72),
    },
    columns: 1,
    orientation: 'portrait' as Orientation,
    content_rect_fp: {
      x_fp: FixedPoint.fromPx(72),
      y_fp: FixedPoint.fromPx(72),
      w_fp: FixedPoint.fromPx(650),  // 794 - 72*2 (A4 at 96 DPI)
      h_fp: FixedPoint.fromPx(979),  // 1123 - 72*2
    }
  },
  LEGAL: {
    page_size: "Legal",
    margins_fp: {
      top: FixedPoint.fromPx(72),
      right: FixedPoint.fromPx(72),
      bottom: FixedPoint.fromPx(72),
      left: FixedPoint.fromPx(72),
    },
    columns: 1,
    orientation: 'portrait' as Orientation,
    content_rect_fp: {
      x_fp: FixedPoint.fromPx(72),
      y_fp: FixedPoint.fromPx(72),
      w_fp: FixedPoint.fromPx(672),  // 816 - 72*2 (Legal at 96 DPI)
      h_fp: FixedPoint.fromPx(1200), // 1344 - 72*2
    }
  }
} as const;

export const DEFAULT_PAGE_SETUP: PageSetup = PAGE_SIZES.LETTER;

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get page dimensions in pixels from PageSetup
 * Matches Rust page_setup.rs::get_page_dimensions_fp logic
 */
export function getPageDimensions(pageSetup: PageSetup): { width: number; height: number } {
  let width: number, height: number;
  
  if (typeof pageSetup.page_size === 'string') {
    // Named page size - dimensions at 96 DPI (WORLD_PX)
    switch (pageSetup.page_size) {
      case 'Letter':
        width = 816;   // 8.5" × 96 DPI
        height = 1056; // 11" × 96 DPI
        break;
      case 'Legal':
        width = 816;   // 8.5" × 96 DPI
        height = 1344; // 14" × 96 DPI
        break;
      case 'A4':
        width = 794;   // 210mm ≈ 8.27" × 96 DPI
        height = 1123; // 297mm ≈ 11.69" × 96 DPI
        break;
      default:
        width = 816;   // Default to Letter
        height = 1056;
    }
  } else {
    // Custom page size
    width = pageSetup.page_size.w_px;
    height = pageSetup.page_size.h_px;
  }
  
  // Apply orientation
  if (pageSetup.orientation === 'landscape') {
    return { width: height, height: width }; // Swap
  }
  
  return { width, height };
}




